import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Avatar,
  Chip,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

export default function FindPartner() {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [bestMatches, setBestMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDocs(collection(db, "users"));
      const allUsers = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const me = allUsers.find((u) => u.id === user.uid);
      setMyProfile(me);
    };
    fetchProfile();
  }, []);

  const handleSearch = async (e) => {
    e && e.preventDefault();
    setLoading(true);
    try {
      let q = collection(db, "users");
      const snapshot = await getDocs(q);
      const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const user = auth.currentUser;
      let me = null;
      if (user) me = allUsers.find(u => u.id === user.uid);
      let filtered = allUsers.filter(u => !me || u.id !== me.id);
      // Smart matching
      let best = [];
      if (me) {
        best = filtered.filter(u =>
          (u.skillsWanted || []).some(sw => (me.skillsOffered || []).includes(sw)) &&
          (me.skillsWanted || []).some(sw => (u.skillsOffered || []).includes(sw))
        );
        filtered = filtered.filter(u => !best.includes(u));
      }
      // Search filter
      if (search.trim()) {
        filtered = filtered.filter(u =>
          (u.skillsOffered || []).some(s => s.toLowerCase().includes(search.trim().toLowerCase())) ||
          (u.skillsWanted || []).some(s => s.toLowerCase().includes(search.trim().toLowerCase()))
        );
        best = best.filter(u =>
          (u.skillsOffered || []).some(s => s.toLowerCase().includes(search.trim().toLowerCase())) ||
          (u.skillsWanted || []).some(s => s.toLowerCase().includes(search.trim().toLowerCase()))
        );
      }
      setBestMatches(best);
      setResults(filtered);
    } catch (err) {
      setResults([]);
      setBestMatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { handleSearch(); }, []);

  const renderUser = (u, highlight) => (
    <Card key={u.id} sx={{ mb: 2, borderRadius: 3, boxShadow: highlight ? 4 : 2, border: highlight ? '2px solid #22c55e' : undefined }}>
      <CardHeader
        avatar={
          <Badge color="success" variant="dot" invisible={!highlight} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Avatar src={u.photoURL} alt={u.calledName ? `${u.calledName}'s photo` : 'Profile photo'}>
              {(u.calledName || u.emailId || u.email || '?')[0].toUpperCase()}
            </Avatar>
          </Badge>
        }
        title={<Typography variant="subtitle1">{u.calledName || u.emailId || u.email || '(not provided)'}</Typography>}
        subheader={u.country || u.timeZone || ''}
      />
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {(u.skillsOffered || []).map(s => <Chip key={s} label={s} color="primary" size="small" />)}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {(u.skillsWanted || []).map(s => <Chip key={s} label={s} color="secondary" size="small" variant="outlined" />)}
        </Stack>
        {highlight && (
          <Typography variant="caption" color="success.main">
            Best Match: You want to learn what they offer, and they want to learn what you offer!
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <TextField
              label="Search by skill or interest"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ 'aria-label': 'Search by skill or interest' }}
            />
            <Button type="submit" variant="contained" color="primary" startIcon={<SearchIcon />}>
              Search
            </Button>
          </form>
        </CardContent>
      </Card>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {bestMatches.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                Best Matches
              </Typography>
              {bestMatches.map(u => renderUser(u, true))}
            </Box>
          )}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Other Partners
          </Typography>
          {results.length > 0 ? (
            results.map(u => renderUser(u, false))
          ) : (
            <Typography color="text.secondary" align="center" sx={{ mt: 3 }}>
              No partners found.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
